import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

const MENU_WIDTH = 272;
const EDGE = 8; // отступ меню от края окна

/**
 * Стрелка «Другие форматы» рядом с главной кнопкой скачивания.
 *
 * Обращение 6f371de1: субтитры, markdown и JSON нужны немногим, а остальным
 * непонятны — поэтому они не кнопками в ряд, а пунктами за маленькой стрелкой
 * без подписи. Главная кнопка (`children`) остаётся как была; стрелка встаёт
 * к ней вплотную и на узком экране не отрывается от неё.
 *
 * 🔴 Меню рисуется в document.body с position: fixed. Панель кнопок вкладки
 * «Расшифровка» на телефоне прокручивается вбок (`overflow-x-auto`), и
 * выпадающий список внутри неё обрезался бы краем панели.
 *
 * Клавиатура — как у кнопки меню по WAI-ARIA: стрелка вниз/вверх открывает и
 * ходит по пунктам по кругу, Home/End; Esc закрывает с возвратом фокуса на
 * стрелку (и на самой стрелке тоже), Tab закрывает и пускает фокус дальше.
 * Недоступный пункт фокусируется и объясняет себя, но не нажимается.
 *
 * @param {Array<{key, label, ext, disabled}>} items - пункты меню
 * @param {Function} onPick - выбор пункта, получает key
 * @param {string} disabledHint - почему пункты недоступны (одна строка под списком)
 * @param {boolean} disabled - стрелка выключена вместе с главной кнопкой
 * @param {string} chevronClassName - вид стрелки под стать главной кнопке
 */
export default function MoreFormatsMenu({ children, items, onPick, disabledHint, disabled = false,
                                          chevronClassName = '' }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);
  const chevronRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  const hintId = useId();
  const anyDisabled = items.some((item) => item.disabled);

  const itemNodes = () => Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') || []);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) chevronRef.current?.focus();
  }, []);

  // Под стрелкой, правым краем к ней; не помещается снизу — над ней.
  const place = useCallback(() => {
    const anchor = chevronRef.current?.getBoundingClientRect();
    if (!anchor) return;
    const width = Math.min(MENU_WIDTH, window.innerWidth - EDGE * 2);
    const height = menuRef.current?.offsetHeight || 0;
    const left = Math.max(EDGE, Math.min(anchor.right - width, window.innerWidth - width - EDGE));
    const below = anchor.bottom + 6;
    const top = height && below + height > window.innerHeight - EDGE && anchor.top - height - 6 >= EDGE
      ? anchor.top - height - 6 : below;
    setPosition({ top, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  // Открылось — фокус на первый доступный пункт (или на первый, если доступных нет).
  const [focusLast, setFocusLast] = useState(false);
  useEffect(() => {
    if (!open) return;
    const nodes = itemNodes();
    const enabled = nodes.filter((node) => node.getAttribute('aria-disabled') !== 'true');
    const target = focusLast ? nodes[nodes.length - 1] : (enabled[0] || nodes[0]);
    target?.focus();
  }, [open, focusLast]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapperRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const openMenu = (last = false) => {
    if (disabled) return;
    setFocusLast(last);
    setOpen(true);
  };

  const onChevronKey = (e) => {
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      close(false);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu(e.key === 'ArrowUp');
    }
  };

  const onMenuKey = (e) => {
    const nodes = itemNodes();
    const index = nodes.indexOf(document.activeElement);
    const go = (i) => { e.preventDefault(); nodes[(i + nodes.length) % nodes.length]?.focus(); };
    if (e.key === 'ArrowDown') go(index + 1);
    else if (e.key === 'ArrowUp') go(index - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(nodes.length - 1);
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
    // Tab не перехватываем (APG menu-button): фокус ставится на стрелку, и
    // браузер своим действием по умолчанию ведёт его к соседу стрелки, а не к
    // концу body, где живёт меню.
    else if (e.key === 'Tab') close();
  };

  const pick = (item) => {
    if (item.disabled) return;
    close();
    onPick(item.key);
  };

  return (
    <div ref={wrapperRef} className="inline-flex shrink-0 items-stretch gap-px">
      {children}
      <button
        ref={chevronRef}
        type="button"
        aria-label="Другие форматы"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={disabled}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={onChevronKey}
        className={`flex items-center justify-center px-[6px] ${chevronClassName}`}
      >
        <ChevronDown size={14} strokeWidth={1.75}
          className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Другие форматы"
          onKeyDown={onMenuKey}
          style={{ top: position.top, left: position.left, width: position.width || MENU_WIDTH }}
          className="fixed z-50 flex flex-col rounded-control bg-paper-card p-[6px] shadow-[0_8px_24px_rgba(0,0,0,.08)]"
        >
          <div role="presentation"
            className="px-[10px] pt-1 pb-[6px] font-mono text-[11px] uppercase tracking-[0.1em] text-paper-ink-3">
            Другие форматы
          </div>
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              tabIndex={-1}
              aria-disabled={item.disabled ? 'true' : undefined}
              aria-describedby={item.disabled && disabledHint ? hintId : undefined}
              onClick={() => pick(item)}
              className={`flex items-center justify-between gap-4 rounded-chip px-[10px] py-2 text-left text-[13px] outline-none ${
                item.disabled
                  ? 'cursor-not-allowed text-paper-hint focus:bg-paper-panel'
                  : 'text-paper-ink hover:bg-paper-panel focus:bg-paper-panel'}`}
            >
              <span>{item.label}</span>
              <span className="font-mono text-[12px] text-paper-hint">.{item.ext}</span>
            </button>
          ))}
          {anyDisabled && disabledHint && (
            <p id={hintId} role="presentation" className="m-0 px-[10px] pt-[6px] pb-1 text-[12px] leading-[1.4] text-paper-ink-3">
              {disabledHint}
            </p>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
